import React from 'react';
import { ExternalLink, Smartphone } from 'lucide-react';
import type { DeepLinkEnvironmentConfig } from '../../../models/MasterAdmin/deepLink';
import { dlBtnGhostClass, dlCardClass } from './deepLinkUi';

type StoreUrls = NonNullable<DeepLinkEnvironmentConfig['storeUrls']>;
type StoreEntry = StoreUrls[keyof StoreUrls];

type Props = {
  open: boolean;
  onClose: () => void;
  linkConfig: DeepLinkEnvironmentConfig | null;
};

const DeepLinkAppLinksModal: React.FC<Props> = ({ open, onClose, linkConfig }) => {
  if (!open) return null;

  const stores: StoreUrls = linkConfig?.storeUrls ?? ({} as StoreUrls);
  const storeEntries = Object.entries(stores) as [string, StoreEntry][];

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close app links"
        onClick={onClose}
      />
      <div className={`relative z-10 w-full max-w-lg ${dlCardClass} p-5 shadow-xl`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">App store links</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Install links for Vedika.Health and Vedika.Health Plus.
            </p>
          </div>
          <button type="button" onClick={onClose} className={dlBtnGhostClass}>
            Close
          </button>
        </div>

        <div className="mt-4 space-y-3">
          {storeEntries.map(([appKey, store]) => (
            <div
              key={appKey}
              className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-800/80"
            >
              <p className="flex items-center gap-2 font-semibold text-gray-900 dark:text-white">
                <Smartphone className="h-4 w-4" />
                {store.label}
              </p>
              <p className="mt-2 break-all text-gray-700 dark:text-gray-200">
                Android:{' '}
                <a
                  href={store.playStoreUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
                >
                  {store.playStoreUrl}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </p>
              <p className="mt-1 break-all text-gray-700 dark:text-gray-200">
                iOS:{' '}
                <a
                  href={store.appStoreUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
                >
                  {store.appStoreUrl}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </p>
            </div>
          ))}
          {!storeEntries.length ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Store links unavailable.</p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DeepLinkAppLinksModal;
